/**
 * Returns the file path without the final extension.
 *
 * @example
 * ```ts
 * filePathWithoutExtension("path/to/foo.baz") // "path/to/foo"
 * ```
 */
export function filePathWithoutExtension(filePath: string): string {
  const components = filePath.split(".");
  if (components.length === 1) {
    return filePath;
  } else {
    return components.slice(0, -1).join(".");
  }
}
/**
 * Returns the last segment (or "stem") of the file path (without the extension).
 *
 * @example
 * ```ts
 * filePathStem("path/to/foo.baz") // "foo"
 * ```
 */
export function filePathStem(filePath: string): string {
  return filePathWithoutExtension(filePath.split("/").pop() || "");
}

export function filePathExtension(filePath: string): string {
  const components = filePath.split(".");
  if (components.length === 1) {
    return "";
  } else {
    return components.pop() || "";
  }
}

export function uniqueFilePath(
  filePath: string,
  existingFilePaths: Set<string>,
): string {
  if (!existingFilePaths.has(filePath)) {
    // short circuit for most common case
    return filePath;
  }
  const baseName = filePathWithoutExtension(filePath);
  const extension = filePathExtension(filePath);

  let index: number = 2;
  const indexMatch = baseName.match(/_(\d+)$/);
  if (indexMatch && indexMatch[1]) {
    index = parseInt(indexMatch[1]) + 1;
  }

  let iterations = 0;
  do {
    filePath = `${baseName}_${index}.${extension}`;
    index++;
    iterations++;
    if (iterations > 100) {
      throw new Error(
        "Could not find a unique file path within a reasonable amount of iterations. " +
          "Stopping to prevent a potential infinite loop.",
      );
    }
  } while (existingFilePaths.has(filePath));
  return filePath;
}

/**
 * Create a new file path by appending a string between the extension (".ext")
 * and the rest of the file path.
 * @example
 *  appendToFilePath("path/to/foo.bar", " copy") // "path/to/foo copy.bar"
 */
export function appendToFileName(filePath: string, str: string): string {
  const baseName = filePathWithoutExtension(filePath);
  const extension = filePathExtension(filePath);
  return `${baseName}${str}.${extension}`;
}

/**
 * Returns the individual segments of a file path that are separated by the
 * "/" delimiter character.
 *
 * @example
 * ```ts
 * getFilePathSegments("path/to/foo.bar") // ["path", "to", "foo.bar"]
 * ```
 */
export function getFilePathSegments(filePath: string): string[] {
  return filePath.split("/");
}

/**
 * Returns the basename of a file path -- the last segment of the file path.
 * Also called the "filename".
 *
 * @example
 * ```ts
 * getFilePathBasename("path/to/foo.bar") // "foo.bar"
 * ```
 */
export function getFilePathBasename(filePath: string): string {
  const segments = getFilePathSegments(filePath);
  // assertion is fine since split() always returns at least one element
  return segments.pop()!;
}

/**
 * Returns the file extension of a file path, including the "." delimiter
 * character.
 *
 * @example
 * ```ts
 * getFilePathExtension("path/to/foo.bar") // ".bar"
 * ```
 */
export function getFilePathExtension(filePath: string): string {
  const basename = getFilePathBasename(filePath);
  const indexOfPeriodChar = basename.lastIndexOf(".");
  if (indexOfPeriodChar === -1) return "";
  return basename.slice(indexOfPeriodChar);
}

export function getFilePathWithoutExtension(filePath: string): string {
  const segments = getFilePathSegments(filePath);
  const basename = segments.pop()!;
  const indexOfPeriodChar = basename.lastIndexOf(".");
  if (indexOfPeriodChar === -1) {
    segments.push(basename);
    return segments.join("/");
  }
  const stem = basename.slice(0, indexOfPeriodChar);
  segments.push(stem);
  return segments.join("/");
}

/**
 * Returns a normalized-version of the file extension.
 *
 * - Lowercase
 * - Prefixed with a "." character
 * - No trailing whitespace
 *
 * @example
 * ```ts
 * normalizeFilePathExtension(".bar") // ".bar"
 * normalizeFilePathExtension(".BAR") // ".bar"
 * normalizeFilePathExtension("BAR") // ".bar"
 * ```
 */
export function normalizeFilePathExtension(extension: string): string {
  if (extension.length === 0) {
    return "";
  }
  if (!extension.startsWith(".")) {
    extension = `.${extension}`;
  }
  return extension.trimEnd().toLowerCase();
}

/**
 * Returns a normalized-version of the file path.
 *
 * - No leading/trailing whitespace
 * - Preserves all casing
 */
export function normalizeFilePath(filePath: string): string {
  return filePath.trim();
}
